import styled, { css } from 'styled-components'
import { backgroundBlue, black, textMuted, white } from '@/theme/colors'
import { flatButton } from '@/theme/recipes'
import { layers, media, radii } from '@/theme/tokens'
import { text } from '@/theme/typography'
import { ErrorBanner } from '@components/Notice'

export interface SortableItemProps {
	$transform?: string
	$transition?: string
	$isDragging: boolean
}

/**
 * How anything sortable wears a drag: dnd-kit's transform and transition, and
 * while it is the one in the air, half opacity on a raised stacking context so
 * it passes over its neighbours rather than under them.
 */
export const sortableItem = css<SortableItemProps>`
	position: relative;
	transform: ${({ $transform }) => $transform ?? 'none'};
	transition: ${({ $transition }) => $transition ?? 'none'};
	opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
	z-index: ${({ $isDragging }) => ($isDragging ? layers.raised : 'auto')};
`

/** The sortable item: the band plus, where the caller has one, an error line under it. */
export const Row = styled.li<SortableItemProps>`
	${sortableItem}
	display: flex;
	flex-direction: column;
	gap: 6px;
	list-style: none;
`

export const RowBand = styled.div<{ $grabbable?: boolean }>`
	${({ $grabbable }) =>
		$grabbable &&
		css`
			cursor: grab;
			/* Pans and taps stay the browser's; the held press is the drag's. */
			touch-action: manipulation;
			/* Text under a press-and-hold would select before the drag starts. */
			user-select: none;
			-webkit-touch-callout: none;

			&:active {
				cursor: grabbing;
			}
		`}
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	height: 52px;
	padding: 0 10px 0 16px;
	background: ${backgroundBlue};
	border-radius: ${radii.inner};

	${media.phone} {
		padding: 0 6px 0 12px;
		gap: 6px;
	}
`

/** Name over its meta line, both truncating. */
export const RowText = styled.span`
	min-width: 0;
	display: flex;
	flex-direction: column;
`

export const RowName = styled.span`
	min-width: 0;
	${text.bodySm}
	color: ${black};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

/** What the row holds or when it starts — "3 events", "9:30 AM" — under its name. */
export const RowMeta = styled.span`
	${text.caption}
	color: ${textMuted};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`

/** The error box under a band whose edit cannot be saved. */
export const RowError = styled(ErrorBanner)`
	margin-top: 4px;
`

export const RowControls = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
	${text.bodyXs}
	color: ${black};

	${media.phone} {
		gap: 2px;
	}
`

export const RemoveButton = styled.button`
	${flatButton}
	width: 28px;
	height: 28px;
	border-radius: ${radii.small};
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: ${textMuted};

	&:hover {
		background: ${white}b3;
		color: ${black};
	}
`

export const DragHandle = styled.button`
	${flatButton}
	width: 28px;
	height: 28px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: ${black};
	opacity: 0.4;
	cursor: grab;
	touch-action: none;

	&:hover {
		opacity: 0.7;
	}

	&:active {
		cursor: grabbing;
	}
`
